import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { QaService } from './service';
import { QaCategoryService } from '../qaCategories/service';
import { ErrorToast, SuccessToast } from '../../shared/toasters/toasters';
import DeleteModal from '../../Common/DeleteModal';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import Pagination from '../../Common/Pagination';

const QaList = () => {
    const { t } = useTranslation(); // Initialize translation hook
    const [list, setList] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [term, setTerm] = useState(''); // Use 'term' instead of 'searchQuery'
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [qaToDelete, setQaToDelete] = useState<number | null>(null);
    const [categoryList, setCategoryList] = useState<any[]>([]); // State for category options
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null); // Selected category ID
    const navigate = useNavigate();

    const entriesPerPageOptions = [
        { label: '5', value: 5 },
        { label: '10', value: 10 },
        { label: '20', value: 20 },
        { label: '30', value: 30 },
        { label: '40', value: 40 },
    ];

    const handleTermChange = (e: any) => {
        setTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleEntriesPerPageChange = (selectedOption: any) => {
        setEntriesPerPage(selectedOption ? selectedOption.value : 10);
        setCurrentPage(1);
    };

    const handleCategoryChange = (selectedOption: any) => {
        setSelectedCategory(selectedOption ? selectedOption.value : null);
        setCurrentPage(1);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const handleDeleteClick = (id: number) => {
        setQaToDelete(id);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (qaToDelete !== null) {
            try {
                await QaService.delete(qaToDelete);
                SuccessToast(t('qa.successDelete'));
                setShowDeleteModal(false);
                setQaToDelete(null);
                // Refresh the list after deletion
                fetchData();
            } catch (error) {
                ErrorToast(error);
            }
        }
    };

    const fetchData = async () => {
        try {
            const response = await QaService.getAll({
                page: currentPage,
                perPage: entriesPerPage,
                term, // Pass term to backend
                category: selectedCategory, // Pass selected category ID to backend
            });
            const data = response.data;
            setList(data);
            setTotalPages(response.totalPages);
        } catch (error) {
            ErrorToast(error);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await QaCategoryService.getAll({ perPage: 0, page: 1 });
          
            const categories = response.data.map((category: any) => ({
                label: category.title,
                value: category.id,
            }));
            setCategoryList(categories);
        } catch (error) {
            ErrorToast(error);
        }
    };

    useEffect(() => {
        fetchCategories(); // Fetch category options on component mount
    }, []);

    useEffect(() => {
        fetchData(); // Fetch QAs whenever filters or pagination change
    }, [currentPage, entriesPerPage, term, selectedCategory]);

    return (
        <React.Fragment>
            <Col>
                <Card>
                    <CardBody className="card-body">
                        <div className='text-end'>
                            <button className='btn btn-primary text-end' onClick={() => navigate('/qas/create')}><i className='ti ti-plus f-18'></i> {t('qa.add')}</button>
                        </div>

                        <div className="d-sm-flex align-items-center mb-3">
                            <ul className="list-inline me-auto my-1">
                                <li className="list-inline-item">
                                    <Select
                                        placeholder={t('qa.entriesPerPage')}
                                        options={entriesPerPageOptions}
                                        onChange={handleEntriesPerPageChange}
                                        defaultValue={entriesPerPageOptions.find(option => option.value === entriesPerPage)}
                                        classNamePrefix="react-select"
                                    />
                                </li>
                                <span> {t('qa.entriesPerPage')}</span>
                            </ul>
                            <ul className="list-inline ms-auto my-1 mt-3">
                                <li className="list-inline-item">
                                    <Select
                                        placeholder={t('qa.selectCategory')}
                                        options={categoryList}
                                        onChange={handleCategoryChange}
                                        isClearable
                                        classNamePrefix="react-select"
                                    />
                                </li>
                                <li className="list-inline-item">
                                    <form className="">
                                        <input
                                            className='form-control'
                                            type="search"
                                            placeholder={t('qa.searchPlaceholder')}
                                            onChange={handleTermChange}
                                        />
                                    </form>
                                </li>
                            </ul>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-hover sticky-actions" id="pc-dt-simple-1">
                                <thead>
                                    <tr>
                                        <th>{t('qa.id')}</th>
                                        <th>{t('qa.category')}</th>
                                        <th>{t('qa.question')}</th>
                            
                                        <th className="text-end sticky-col">{t('qa.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.map((item: any, index: number) => (
                                        <tr key={index}>
                                            <td>{item.id}</td>
                                            <td>{item.category?.title}</td>
                                            <td className="wrap-col">{item.question}</td>
                                        
                                            <td className="text-end text-nowrap sticky-col">
                                                <ul className="list-inline mb-0">
                                                    <li className="list-inline-item" onClick={() => navigate(`/qas/edit/${item.id}`)}>
                                                        <a href="#" className="avtar avtar-s btn-link-success btn-pc-default">
                                                            <i className="ti ti-edit f-20"></i>
                                                        </a>
                                                    </li>
                                                    <li className="list-inline-item">
                                                        <span
                                                            role="button"
                                                            className="avtar avtar-s btn-link-danger btn-pc-default"
                                                            onClick={() => handleDeleteClick(item.id)}
                                                        >
                                                            <i className="ti ti-trash f-20"></i>
                                                        </span>
                                                    </li>
                                                </ul>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                          previousLabel={t('qa.previous')}
                          nextLabel={t('qa.next')}
                        />
                    </CardBody>
                </Card>
            </Col>

            <DeleteModal
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleDeleteId={handleDeleteConfirm}
                name={t('qa.name')}
            />
        </React.Fragment>
    );
};

export default QaList;